/**
 * The Tasks section: My tasks as a list, a shared list as rows or as a
 * board, one task opened over either, and "make a task" from somewhere else.
 *
 * A task is any work someone owes -- a call to make, an invoice to send, a
 * contract to read. Runs entirely on `TasksSource` (`source.ts`).
 */
import {
  Button,
  Callout,
  Empty,
  Icon,
  Segmented,
  SegmentedOption,
  Skeleton,
} from '@wtfalch/design';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { TasksLocation } from '../routes.ts';
import { longDay, todayIso } from './dates.ts';
import { ListBoard } from './ListBoard.tsx';
import { type MakeFrom, MakeTask } from './MakeTask.tsx';
import { Quick } from './Quick.tsx';
import { type Group, GroupedRows, groupByDay, Who } from './Rows.tsx';
import { MINE, type Person, type Task, type TaskList, type TasksSource } from './source.ts';
import { TaskPage } from './TaskPage.tsx';
import './tasks.css';

export type MakingTask = { from: MakeFrom; text: string; initialTitle: string };

const NUMBER_WORDS = [
  'no one',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
];

function peopleWords(count: number): string {
  return `${NUMBER_WORDS[count] ?? count} ${count === 1 ? 'person' : 'people'}`;
}

/** One group a day, for "By day": the date as its heading. */
function groupByDate(tasks: readonly Task[]): Group[] {
  const byDay = new Map<string, Task[]>();
  for (const task of tasks.filter((t) => !t.done)) {
    const key = task.dueOn ?? 'none';
    byDay.set(key, [...(byDay.get(key) ?? []), task]);
  }
  return [...byDay.entries()]
    .sort(([a], [b]) => (a === 'none' ? 1 : b === 'none' ? -1 : a.localeCompare(b)))
    .map(([key, list]) => ({ key, label: key === 'none' ? 'No date' : longDay(key), tasks: list }));
}

export function Tasks({
  source,
  location,
  onNavigate,
  today = todayIso(),
  making = null,
  onMakingDone,
}: {
  source: TasksSource;
  location: TasksLocation;
  onNavigate: (location: TasksLocation) => void;
  today?: string;
  /** Set to open "Make a task" over the section -- from a message or a thread. */
  making?: MakingTask | null;
  onMakingDone?: (made: Task | null) => void;
}) {
  const [lists, setLists] = useState<readonly TaskList[] | null>(null);
  const [tasks, setTasks] = useState<readonly Task[] | null>(null);
  const [me, setMe] = useState<Person | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const [layout, setLayout] = useState<'list' | 'day'>('list');
  const quickRef = useRef<HTMLInputElement>(null);
  const listId = location.listId;

  // A new read of everything the screen shows, after any change. A failure
  // is a message on the page, never a thrown render.
  useEffect(() => {
    let live = true;
    void version;
    // `me` last: the name it carries is one the other two have just read.
    Promise.all([source.lists(), source.tasks(listId)])
      .then(async ([l, t]) => {
        const m = await source.me();
        if (!live) return;
        setLists(l);
        setTasks(t);
        setMe(m);
        setError(null);
      })
      .catch((err: unknown) => {
        // Not the SDK's own message: a wire error's text is the service's,
        // not written for a person to read (agora#486). Logged (agora#498)
        // so the failure leaves a trace even though the text on screen stays
        // generic.
        console.error(err);
        if (!live) return;
        // Also drop the previous list's rows (never `lists`, which the
        // sidebar still needs to navigate away from this one) -- otherwise a
        // read that fails after switching lists left the old list's rows on
        // screen under the error, looking like they belonged to the new one.
        setTasks(null);
        setError('Tasks could not be read.');
      });
    return () => {
      live = false;
    };
  }, [source, listId, version]);

  const refresh = useCallback(() => setVersion((n) => n + 1), []);
  const current = lists?.find((l) => l.id === listId) ?? null;
  const shared = lists?.filter((l) => l.id !== MINE) ?? [];
  const isMine = listId === MINE;
  const open = (task: Task) => onNavigate({ listId, taskId: task.id });
  const act = async (run: () => Promise<unknown>) => {
    try {
      await run();
      refresh();
    } catch {
      setError('The change was not saved.');
    }
  };
  const groups = useMemo(() => {
    if (!tasks) return [];
    return isMine && layout === 'day' ? groupByDate(tasks) : groupByDay(tasks, today);
  }, [tasks, isMine, layout, today]);

  const link = (list: TaskList) => (
    <li key={list.id}>
      <button
        type="button"
        className="tasks-list-link"
        aria-current={list.id === listId ? 'page' : undefined}
        onClick={() => onNavigate({ listId: list.id, taskId: null })}
      >
        <span className="tasks-list-name">{list.name}</span>
        <span className="tasks-count">{list.open}</span>
      </button>
    </li>
  );

  const openCount = tasks?.filter((t) => !t.done).length ?? 0;
  const doneCount = tasks?.filter((t) => t.done).length ?? 0;

  return (
    <div className="tasks" data-task-open={location.taskId ? true : undefined}>
      <nav className="tasks-side" aria-label="Lists">
        <h3 className="tasks-side-head">Lists</h3>
        <ul>{lists ? lists.filter((l) => l.id === MINE).map(link) : null}</ul>
        {shared.length > 0 && (
          <>
            <h3 className="tasks-side-head">Shared with others</h3>
            <ul>{shared.map(link)}</ul>
          </>
        )}
      </nav>

      <div className="tasks-main">
        <header className="tasks-head">
          <div className="tasks-head-text">
            <h2>{current?.name ?? (isMine ? 'My tasks' : '')}</h2>
            {tasks && (
              <p>
                {isMine
                  ? `${openCount} open · everything you owe, from any place`
                  : `A list for ${peopleWords(current?.members.length ?? 0)} · ${openCount} open, ${doneCount} done`}
              </p>
            )}
          </div>
          {current && current.members.length > 0 && (
            <span
              className="tasks-members"
              role="img"
              aria-label={current.members.map((m) => m.name).join(', ')}
            >
              {current.members.map((m) => (
                <Who key={m.id} person={m} size="sm" />
              ))}
            </span>
          )}
          {isMine ? (
            <Segmented
              label="Layout"
              size="sm"
              value={layout}
              onChange={(v) => setLayout(v === 'day' ? 'day' : 'list')}
            >
              <SegmentedOption value="list">List</SegmentedOption>
              <SegmentedOption value="day">By day</SegmentedOption>
            </Segmented>
          ) : (
            <Button
              kind="default"
              size="sm"
              onPress={() =>
                onNavigate({ listId, taskId: null, ...(location.board ? {} : { board: true }) })
              }
            >
              {location.board ? 'View as list' : 'View as board'}
            </Button>
          )}
          <Button kind="primary" size="sm" onPress={() => quickRef.current?.focus()}>
            <Icon name="plus" size={14} />
            New task
          </Button>
        </header>

        {error && <Callout tone="bad">{error}</Callout>}

        {!tasks ? (
          error ? null : (
            <Skeleton lines={6} />
          )
        ) : (
          <div className="tasks-body">
            {!location.board && (
              <Quick
                ref={quickRef}
                label={isMine ? 'Add a task' : `Add to ${current?.name ?? 'this list'}`}
                placeholder={
                  isMine
                    ? 'Add a task… e.g. ‘Call the bank Friday’'
                    : `Add to ${current?.name ?? 'this list'}`
                }
                today={today}
                onAdd={async (task) => {
                  await source.create({ ...task, listId });
                  refresh();
                }}
              />
            )}
            {location.board && current ? (
              <ListBoard
                list={current}
                tasks={tasks}
                today={today}
                onOpen={open}
                onMove={(task, statusId) => act(() => source.moveTo(task.id, statusId))}
                onAdd={async (statusId, task) => {
                  const made = await source.create({ ...task, listId });
                  // The task exists once create answers: a failed move leaves it in
                  // its first column, on screen, so adding it again never duplicates it.
                  try {
                    await source.moveTo(made.id, statusId);
                  } catch (error) {
                    console.warn(
                      'Tasks: a new task could not be moved to its column',
                      (error as Error)?.name,
                    );
                  } finally {
                    refresh();
                  }
                }}
              />
            ) : groups.length === 0 ? (
              <Empty icon="check">
                {isMine ? 'Nothing you owe right now.' : 'Nothing in this list yet.'}
              </Empty>
            ) : (
              <GroupedRows
                groups={groups}
                today={today}
                showWho={!isMine}
                openId={location.taskId}
                onOpen={open}
                onToggle={(task, done) => void act(() => source.setDone(task, done))}
              />
            )}
          </div>
        )}
      </div>

      {location.taskId && (
        <TaskPage
          source={source}
          taskId={location.taskId}
          lists={lists ?? []}
          today={today}
          version={version}
          onClose={() =>
            onNavigate({ listId, taskId: null, ...(location.board ? { board: true } : {}) })
          }
          onChanged={refresh}
        />
      )}

      {making && lists && me && (
        <MakeTask
          source={source}
          lists={lists}
          from={making.from}
          text={making.text}
          initialTitle={making.initialTitle}
          me={me}
          today={today}
          onCancel={() => onMakingDone?.(null)}
          onMade={(task) => {
            refresh();
            onMakingDone?.(task);
          }}
        />
      )}
    </div>
  );
}
